import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, MapPin, Star, Globe, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CourseCard } from "@/components/CourseCard";
import { initials, resolveImg } from "@/lib/format";

export default function UniversityDetail() {
  const { slug } = useParams();
  const [uni, setUni] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/public/universities/${slug}`).then((r) => setUni(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-heading text-2xl font-bold text-slate-900">University not found</h1>
      <Button asChild className="mt-6 bg-blue-800 hover:bg-blue-900"><Link to="/universities">Browse universities</Link></Button>
    </div>
  );
  if (!uni) return <div className="mx-auto max-w-5xl px-4 py-20"><div className="h-64 animate-pulse rounded-xl bg-slate-200" /></div>;

  const courses = (uni.courses || []).map((c) => ({ ...c, institution: {
    id: uni.id, name: uni.name, slug: uni.slug, logo: uni.logo, website: uni.website,
    plan: uni.plan, featured: uni.featured, country: uni.country, city: uni.city } }));

  const visit = async () => {
    try { await api.post(`/public/track/website-click/${uni.id}`); } catch {}
    window.open(uni.website, "_blank", "noopener");
  };

  return (
    <div className="bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        <Link to="/universities" className="mb-4 inline-flex items-center text-sm text-slate-500 hover:text-blue-800">
          <ChevronLeft className="h-4 w-4" /> All universities
        </Link>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              {uni.logo ? (
                <img src={resolveImg(uni.logo)} alt={uni.name} className="h-20 w-20 rounded-lg object-cover" />
              ) : (
                <span className="flex h-20 w-20 items-center justify-center rounded-lg bg-blue-50 font-heading text-2xl font-bold text-blue-800">{initials(uni.name)}</span>
              )}
              <div>
                {uni.featured && (
                  <span className="mb-1 inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-700">
                    <Star className="h-3 w-3 fill-current" /> Featured University
                  </span>
                )}
                <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">{uni.name}</h1>
                <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                  <MapPin className="h-4 w-4" /> {[uni.city, uni.country].filter(Boolean).join(", ")} · {uni.type}
                </p>
              </div>
            </div>
            {uni.website && (
              <Button onClick={visit} className="bg-blue-800 hover:bg-blue-900" data-testid="visit-university-btn">
                Visit Website <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>

          {uni.description && <p className="mt-6 text-slate-600">{uni.description}</p>}
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-500">
            {uni.accreditation && <span><Globe className="mr-1 inline h-4 w-4" />{uni.accreditation}</span>}
            {uni.address && <span><MapPin className="mr-1 inline h-4 w-4" />{uni.address}</span>}
          </div>
        </div>

        <div className="mt-8">
          <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">Online Programmes ({courses.length})</h2>
          {courses.length ? (
            <div className="mt-4 grid gap-5 md:grid-cols-2">
              {courses.map((c) => <CourseCard key={c.id} course={c} />)}
            </div>
          ) : (
            <p className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
              No published programmes yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
